import React, { useState, useEffect, useRef } from 'react';
import { Plus, MapPin, Globe, Search, Edit, ChevronDown, ChevronRight } from 'lucide-react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { purchasesAPI, componentsAPI, uploadsAPI } from '../services/api';
import ImagePreviewModal from '../components/ImagePreviewModal';
import PdfThumbnail from '../components/PdfThumbnail';
import { formatLocalDate } from '../services/date';

const Purchases = () => {
  const [purchases, setPurchases] = useState([]);
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [imagesByPurchase, setImagesByPurchase] = useState({});
  const [editing, setEditing] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [filePreview, setFilePreview] = useState(null);
  const [collapsedCategories, setCollapsedCategories] = useState({ brass: true, bullet: true, primer: true, powder: true });
  const hasAutoCollapsedRef = useRef(false);
  
  const { register, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm();
  const watchQuantity = watch('quantity');
  const watchTotalCost = watch('total_cost');
  const watchUnitCost = watch('unit_cost');
  const watchComponentId = watch('component_id');
  const [lastEdited, setLastEdited] = useState(null); // 'unit' | 'total'
  // Separate form instance for the Edit modal to avoid leaking values into the create form
  const { register: registerEdit, handleSubmit: handleSubmitEdit, reset: resetEdit, watch: watchEdit, setValue: setValueEdit } = useForm();
  const selectedComp = components.find(c => c.id === Number(watchComponentId));

  useEffect(() => {
    loadData();
  }, []);

  // If total cost edited, derive unit cost
  useEffect(() => {
    const quantity = parseFloat(watchQuantity) || 0;
    const totalCost = parseFloat(watchTotalCost) || 0;
    if (lastEdited === 'total' || (lastEdited === null && totalCost && !watchUnitCost)) {
      const unitCost = quantity > 0 ? (totalCost / quantity) : 0;
      setValue('unit_cost', (isFinite(unitCost) ? unitCost : 0).toFixed(2));
    }
  }, [watchQuantity, watchTotalCost, lastEdited, watchUnitCost, setValue]);

  // If unit cost edited, derive total cost
  useEffect(() => {
    const quantity = parseFloat(watchQuantity) || 0;
    const unitCost = parseFloat(watchUnitCost) || 0;
    if (lastEdited === 'unit' || (lastEdited === null && unitCost && !watchTotalCost)) {
    const totalCost = quantity * unitCost;
      setValue('total_cost', (isFinite(totalCost) ? totalCost : 0).toFixed(2));
    }
  }, [watchQuantity, watchUnitCost, lastEdited, watchTotalCost, setValue]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [purchasesRes, componentsRes] = await Promise.all([
        purchasesAPI.getAll(),
        componentsAPI.getAll()
      ]);
      setPurchases(purchasesRes.data);
      setComponents(componentsRes.data);
      // Fetch images for each purchase (best-effort)
      try {
        const results = await Promise.all(
          purchasesRes.data.map(p => uploadsAPI.list('purchase', p.id).then(r => ({ id: p.id, images: r.data })).catch(() => ({ id: p.id, images: [] })))
        );
        const map = {};
        results.forEach(r => { map[r.id] = r.images; });
        setImagesByPurchase(map);
      } catch {}
    } catch (error) {
      console.error('Error loading purchases:', error);
      toast.error('Failed to load purchases');
    } finally {
      setLoading(false);
      // Auto-collapse only on initial load
      if (!hasAutoCollapsedRef.current) {
        hasAutoCollapsedRef.current = true;
        setCollapsedCategories({ brass: true, bullet: true, primer: true, powder: true });
      }
    }
  };

  const toggleCategory = (type) => {
    setCollapsedCategories(prev => ({ ...prev, [type]: !prev[type] }));
  };

  const collapseAll = () => {
    setCollapsedCategories({ brass: true, bullet: true, primer: true, powder: true });
  };

  const expandAll = () => {
    setCollapsedCategories({ brass: false, bullet: false, primer: false, powder: false });
  };

  const onSubmit = async (data) => {
    try {
      await purchasesAPI.create({
        ...data,
        quantity: parseInt(data.quantity),
        unit_cost: parseFloat(data.unit_cost),
        total_cost: parseFloat(data.total_cost),
        starting_firing_count: data.starting_firing_count != null ? parseInt(data.starting_firing_count) : undefined
      });
      toast.success('Purchase recorded successfully');
      setShowForm(false);
      reset();
      loadData();
    } catch (error) {
      console.error('Error creating purchase:', error);
      toast.error('Failed to record purchase');
    }
  };

  const openEdit = (purchase) => {
    setEditing(purchase);
    resetEdit({
      component_id: purchase.component?.id,
      quantity: purchase.quantity,
      unit: purchase.unit,
      unit_cost: purchase.unit_cost,
      total_cost: purchase.total_cost,
      purchase_location: purchase.purchase_location || '',
      website: purchase.website || '',
      purchase_date: purchase.purchase_date?.slice(0,10),
      notes: purchase.notes || '',
      starting_firing_count: (purchase.starting_firing_count ?? 0)
    });
    setShowEditModal(true);
  };

  const submitEdit = async (form) => {
    if (!editing) return;
    try {
      const res = await purchasesAPI.update(editing.id, {
        quantity: Number(form.quantity),
        unit: form.unit,
        unit_cost: form.unit_cost != null ? Number(form.unit_cost) : undefined,
        total_cost: form.total_cost != null ? Number(form.total_cost) : undefined,
        purchase_location: form.purchase_location,
        website: form.website,
        purchase_date: form.purchase_date,
        notes: form.notes,
        starting_firing_count: form.starting_firing_count != null ? parseInt(form.starting_firing_count) : undefined,
      });
      
      // Check if purchase was deleted (quantity set to 0)
      if (res?.data?.deleted) {
        toast.success('Purchase deleted');
      } else {
        toast.success('Purchase updated');
      }
      
      setShowEditModal(false);
      setEditing(null);
      resetEdit({});
      loadData();
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.message || 'Failed to update purchase';
      toast.error(msg);
    }
  };

  // Open/close helpers for create form: always clear state
  const openCreate = () => { reset({}); setLastEdited(null); setShowForm(true); };
  const closeCreate = () => { setShowForm(false); reset({}); setLastEdited(null); };

  const handleUpload = async (purchaseId, files) => {
    const fileArr = Array.from(files || []);
    if (fileArr.length === 0) return;
    try {
      await uploadsAPI.upload('purchase', purchaseId, fileArr);
      const res = await uploadsAPI.list('purchase', purchaseId);
      setImagesByPurchase(prev => ({ ...prev, [purchaseId]: res.data }));
      toast.success('Images uploaded');
    } catch {
      toast.error('Failed to upload images');
    }
  };

  const removeImage = async (purchaseId, imageId) => {
    try {
      await uploadsAPI.remove(imageId);
      setImagesByPurchase(prev => ({ ...prev, [purchaseId]: (prev[purchaseId] || []).filter(i => i.id !== imageId) }));
      toast.success('Image deleted');
    } catch {
      toast.error('Failed to delete image');
    }
  };

  const getTypeIcon = (typeName) => {
    switch (typeName.toLowerCase()) {
      case 'brass':
        return '🥉';
      case 'primer':
        return '🔥';
      case 'bullet':
        return '🎯';
      case 'powder':
        return '💨';
      default:
        return '📦';
    }
  };

  const getTypeColor = (typeName) => {
    switch (typeName.toLowerCase()) {
      case 'brass':
        return 'bg-yellow-100 text-yellow-800';
      case 'primer':
        return 'bg-red-100 text-red-800';
      case 'bullet':
        return 'bg-blue-100 text-blue-800';
      case 'powder':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // aggregate totals moved into stats below; remove unused per linter

  // Aggregate stats by component type
  const typeList = ['brass', 'bullet', 'primer', 'powder'];
  const unitLabel = (type) => (type === 'powder' ? 'lbs' : 'pcs');
  const statsByType = typeList.map((type) => {
    const items = purchases.filter(p => (p.component?.type || '').toLowerCase() === type);
    const spent = items.reduce((s,p) => s + (Number(p.total_cost) || 0), 0);
    const count = items.length;
    const qty = items.reduce((s,p) => s + (Number(p.quantity) || 0), 0);
    return { type, items, spent, count, qty };
  });

  const filteredPurchases = purchases.filter((p) => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    const fields = [
      p.component?.brand,
      p.component?.model,
      p.component?.caliber,
      p.component?.type,
      p.purchase_location,
      p.website,
      p.unit,
      p.notes,
      p.quantity?.toString(),
      p.unit_cost?.toString(),
      p.total_cost?.toString(),
      p.remaining_quantity?.toString(),
      p.purchase_date ? formatLocalDate(p.purchase_date) : ''
    ];
    return fields.filter(Boolean).some((val) => val.toString().toLowerCase().includes(s));
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Purchases</h1>
          <p className="text-gray-600 mt-2">Track your component purchases and costs</p>
        </div>
        <button
          onClick={openCreate}
          className="btn btn-primary flex items-center space-x-2"
        >
          <Plus className="h-4 w-4" />
          <span>Record Purchase</span>
        </button>
      </div>

      {/* Add Purchase Form (moved above summary) */}
      {showForm && (
        <div className="card p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Record New Purchase</h3>
            <button
              onClick={closeCreate}
              className="text-gray-400 hover:text-gray-600"
            >
              ×
            </button>
          </div>
          
          <form onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="label">Component</label>
              <select
                {...register('component_id', { required: 'Component is required' })}
                className="input"
              >
                <option value="">Select component...</option>
                {components.map(component => (
                  <option key={component.id} value={component.id}>
                    {(component.component_type?.name || '').toLowerCase() === 'brass'
                      ? `${component.head_stamp || component.model || component.brand} (${component.caliber}) - Brass`
                      : `${component.brand} ${component.model} (${component.caliber}) - ${component.component_type.name}`}
                  </option>
                ))}
              </select>
              {errors.component_id && (
                <p className="text-red-500 text-sm mt-1">{errors.component_id.message}</p>
              )}
            </div>

            <div>
              <label className="label">Quantity</label>
              <input
                type="number"
                {...register('quantity', { 
                  required: 'Quantity is required',
                  min: { value: 1, message: 'Quantity must be at least 1' }
                })}
                className="input"
                placeholder="e.g., 1000"
              />
              {errors.quantity && (
                <p className="text-red-500 text-sm mt-1">{errors.quantity.message}</p>
              )}
            </div>

            {selectedComp && (selectedComp.component_type?.name || '').toLowerCase() === 'brass' && (
              <div>
                <label className="label">Starting Firing Count (brass)</label>
                <input
                  type="number"
                  min="0"
                  className="input"
                  {...register('starting_firing_count')}
                  placeholder="e.g., 0 for new, 1+ for used cases"
                />
              </div>
            )}

            <div>
              <label className="label">Total Cost ($)</label>
              <input
                type="number"
                step="0.01"
                {...register('total_cost', { 
                  required: 'Total cost is required',
                  min: { value: 0, message: 'Total cost must be positive' }
                })}
                className="input"
                placeholder="e.g., 150.00"
                onChange={(e) => { setLastEdited('total'); setValue('total_cost', e.target.value); }}
              />
              {errors.total_cost && (
                <p className="text-red-500 text-sm mt-1">{errors.total_cost.message}</p>
              )}
            </div>

            <div>
              <label className="label">Unit Cost ($)</label>
              <input
                type="number"
                step="0.01"
                {...register('unit_cost')}
                className="input"
                onChange={(e) => { setLastEdited('unit'); setValue('unit_cost', e.target.value); }}
              />
            </div>

            <div>
              <label className="label">Purchase Date</label>
              <input
                type="date"
                {...register('purchase_date', { required: 'Purchase date is required' })}
                className="input"
                defaultValue={new Date().toISOString().split('T')[0]}
              />
              {errors.purchase_date && (
                <p className="text-red-500 text-sm mt-1">{errors.purchase_date.message}</p>
              )}
            </div>

            <div>
              <label className="label">Purchase Location</label>
              <input
                type="text"
                {...register('purchase_location')}
                className="input"
                placeholder="e.g., Local Gun Store"
              />
            </div>

            <div className="md:col-span-2">
              <label className="label">Website</label>
              <input
                type="url"
                {...register('website')}
                className="input"
                placeholder="e.g., https://example.com"
              />
            </div>

            <div className="md:col-span-2">
              <label className="label">Notes</label>
              <textarea
                {...register('notes')}
                className="input"
                rows="3"
                placeholder="Additional notes about this purchase..."
              />
            </div>

            <div className="md:col-span-2 flex space-x-3">
              <button type="submit" className="btn btn-primary">
                Record Purchase
              </button>
              <button
                type="button"
                onClick={closeCreate}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Search */}
      <div className="flex">
        <div className="relative w-full md:w-1/2">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search purchases..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input pl-10 w-full"
          />
          </div>
        </div>

      {/* Summary Stats by Component Category */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {statsByType.map(({ type, spent, count, qty }) => (
          <div key={type} className="card p-6">
          <div className="flex items-center justify-between">
            <div>
                <p className="text-sm font-medium text-gray-600 capitalize">{type}</p>
                <p className="text-xs text-gray-500">{count} purchases</p>
            </div>
              <div className="text-2xl">{getTypeIcon(type)}</div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
              <div>
                <div className="text-gray-600">Spent</div>
                <div className="font-semibold text-green-600">${spent.toFixed(2)}</div>
        </div>
            <div>
                <div className="text-gray-600">Qty</div>
                <div className="font-semibold text-purple-600">{qty.toLocaleString()} {unitLabel(type)}</div>
            </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add Purchase Form (was below; removed here) */}

      {/* Purchases List */}
      <div className="space-y-4">
        {/* Collapse/Expand All Buttons */}
        <div className="flex items-center gap-2">
          <button
            onClick={collapseAll}
            className="btn btn-secondary btn-sm"
          >
            Collapse All
          </button>
          <button
            onClick={expandAll}
            className="btn btn-secondary btn-sm"
          >
            Expand All
          </button>
        </div>

        {(() => {
          const byType = {};
          filteredPurchases.forEach(p => {
            const t = (p.component?.type || 'Other').toLowerCase();
            if (!byType[t]) byType[t] = [];
            byType[t].push(p);
          });
          const typeOrder = ['brass','bullet','primer','powder'];
          return Object.entries(byType)
            .sort((a,b)=> typeOrder.indexOf(a[0]) - typeOrder.indexOf(b[0]))
            .map(([type, list]) => {
              const byComp = {};
              list.forEach(p => {
                const key = `${p.component?.id || 'unknown'}`;
                if (!byComp[key]) byComp[key] = { comp: p.component, items: [] };
                byComp[key].items.push(p);
              });
              const isCollapsed = collapsedCategories[type];
              return (
                <div key={type} className="space-y-3">
                  <div
                    className="text-lg font-semibold text-gray-900 flex items-center gap-2 mt-2 cursor-pointer select-none hover:bg-gray-100 p-2 rounded"
                    onClick={() => toggleCategory(type)}
                  >
                    {isCollapsed ? <ChevronRight className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                    <span>{getTypeIcon(type)}</span>
                    <span className={`px-2 py-1 text-xs rounded-full ${getTypeColor(type)}`}>{type}</span>
                    <span className="text-sm text-gray-500">({list.length} purchases)</span>
                  </div>
                  {!isCollapsed && Object.values(byComp).map(({comp, items}) => (
                    <div key={comp?.id || Math.random()} className="card p-4">
                      <div className="mb-2">
                        <div className="font-semibold text-gray-900">
                          {(comp?.type || '').toLowerCase() === 'brass'
                            ? (comp?.head_stamp || comp?.model || comp?.brand)
                            : `${comp?.brand || ''} ${comp?.model || ''}`}
                        </div>
                        <div className="text-sm text-gray-600">{comp?.caliber}</div>
                      </div>
                      <div className="space-y-3">
                        {items.sort((a,b)=> new Date(a.purchase_date) - new Date(b.purchase_date)).map(purchase => (
                          <div key={purchase.id} className="border rounded p-3">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Quantity:</span>
                                    <p className="font-medium">{purchase.quantity.toLocaleString()} {purchase.unit}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">Unit Cost:</span>
                                    <p className="font-medium">${purchase.unit_cost.toFixed(2)}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">Remaining:</span>
                                    <p className="font-medium text-green-600">{purchase.remaining_quantity.toLocaleString()} {purchase.unit}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">Date:</span>
                                    <p className="font-medium">{formatLocalDate(purchase.purchase_date)}</p>
                    </div>
                  </div>
                  {(purchase.purchase_location || purchase.website) && (
                                  <div className="flex items-center space-x-4 mt-2 text-sm text-gray-600">
                      {purchase.purchase_location && (
                                      <div className="flex items-center space-x-1"><MapPin className="h-4 w-4" /><span>{purchase.purchase_location}</span></div>
                      )}
                      {purchase.website && (
                                      <div className="flex items-center space-x-1"><Globe className="h-4 w-4" /><a href={purchase.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">Website</a></div>
                      )}
                    </div>
                  )}
                  {purchase.notes && (
                                  <div className="mt-2 text-sm text-gray-600">{purchase.notes}</div>
                                )}
                                {/* Files (images and PDFs) */}
                                <div className="mt-2">
                                  <div className="flex items-center gap-2 mb-2">
                                    <label className="btn btn-secondary btn-sm cursor-pointer">
                                      Upload Files
                                      <input type="file" accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.odp,.rtf,.txt,.csv,.json,.md,.log" multiple className="hidden" onChange={(e) => handleUpload(purchase.id, e.target.files)} />
                                    </label>
                                  </div>
                                  {(imagesByPurchase[purchase.id] || []).length > 0 ? (
                                    <div className="flex flex-wrap gap-3">
                                      {(imagesByPurchase[purchase.id] || []).map(file => {
                                        const src = file.download_url || file.url;
                                        const isPdf = (file.filename || '').toLowerCase().endsWith('.pdf') || (src || '').toLowerCase().endsWith('.pdf');
                                        return (
                                          <div key={file.id} className="relative group">
                                            {isPdf ? (
                                              <PdfThumbnail
                                                fileId={file.id}
                                                url={src}
                                                thumbnailUrl={file.thumbnail_url}
                                                filename={file.filename}
                                                sizeClass="w-40 h-56"
                                                onClick={() => setFilePreview({ id: file.id, url: src, filename: file.filename, thumbnailUrl: file.thumbnail_url })}
                                              />
                                            ) : (
                                              (() => {
                                                const isImg = /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test((file.filename || ''));
                                                const thumb = isImg ? src : (file.thumbnail_url || src);
                                                return (
                                                  <img src={thumb} alt={file.filename} className="w-24 h-24 object-contain bg-white rounded shadow cursor-pointer" onClick={() => setFilePreview({ id: file.id, url: src, filename: file.filename, thumbnailUrl: file.thumbnail_url })} />
                                                );
                                              })()
                                            )}
                                            <div className="flex justify-between mt-1 text-xs text-gray-700">
                                              <button className="underline" onClick={() => setFilePreview({ id: file.id, url: src, filename: file.filename, thumbnailUrl: file.thumbnail_url })}>Open</button>
                                              <button className="text-red-600 underline" onClick={() => removeImage(purchase.id, file.id)}>Delete</button>
                                            </div>
                                          </div>
                                        );
                                      })}
                    </div>
                                  ) : (
                                    <p className="text-xs text-gray-500">No files uploaded.</p>
                  )}
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-green-600">${purchase.total_cost.toFixed(2)}</p>
                <p className="text-sm text-gray-600">Total Cost</p>
                                <button className="btn btn-secondary btn-sm mt-2" title="Edit" onClick={() => openEdit(purchase)}>
                                  <Edit className="h-4 w-4" />
                                </button>
              </div>
            </div>
          </div>
        ))}
                      </div>
                    </div>
                  ))}
                </div>
              );
            });
        })()}
      </div>

      {/* Edit Purchase Modal */}
      {showEditModal && editing && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Edit Purchase</h3>
                <button onClick={() => { setShowEditModal(false); setEditing(null); resetEdit({}); }} className="text-gray-400 hover:text-gray-600">×</button>
              </div>
              <form onSubmit={handleSubmitEdit(submitEdit)} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">Quantity</label>
                  <input type="number" step="any" className="input" {...registerEdit('quantity', { required: true, min: Math.max(0, (editing.quantity - editing.remaining_quantity)) })}
                    onChange={(e) => {
                      const qStr = e.target.value;
                      setValueEdit('quantity', qStr);
                      const q = parseFloat(qStr) || 0;
                      const uc = parseFloat(watchEdit('unit_cost')) || 0;
                      const total = q * uc;
                      setValueEdit('total_cost', (isFinite(total) ? total : 0).toFixed(2));
                    }}
                  />
                  <p className="text-xs text-gray-500 mt-1">Min: {(editing.quantity - editing.remaining_quantity).toLocaleString()} {editing.unit} already used</p>
                </div>
                <div>
                  <label className="label">Unit</label>
                  <select className={`input ${((editing.quantity - editing.remaining_quantity) > 0) ? 'bg-gray-100 cursor-not-allowed' : ''}`} {...registerEdit('unit')} disabled={(editing.quantity - editing.remaining_quantity) > 0}>
                    <option value="pieces">pieces</option>
                    <option value="grains">grains</option>
                    <option value="pounds">pounds</option>
                  </select>
                </div>
                <div>
                  <label className="label">Unit Cost ($)</label>
                  <input type="number" step="0.01" className="input" {...registerEdit('unit_cost')}
                    onChange={(e) => {
                      const ucStr = e.target.value;
                      setValueEdit('unit_cost', ucStr);
                      const q = parseFloat(watchEdit('quantity')) || 0;
                      const uc = parseFloat(ucStr) || 0;
                      const total = q * uc;
                      setValueEdit('total_cost', (isFinite(total) ? total : 0).toFixed(2));
                    }}
                  />
                </div>
                <div>
                  <label className="label">Total Cost ($)</label>
                  <input type="number" step="0.01" className="input" {...registerEdit('total_cost')}
                    onChange={(e) => {
                      const totalStr = e.target.value;
                      setValueEdit('total_cost', totalStr);
                      const q = parseFloat(watchEdit('quantity')) || 0;
                      const total = parseFloat(totalStr) || 0;
                      const uc = q > 0 ? (total / q) : 0;
                      setValueEdit('unit_cost', (isFinite(uc) ? uc : 0).toFixed(2));
                    }}
                  />
                </div>
                <div>
                  <label className="label">Purchase Date</label>
                  <input type="date" className="input" {...registerEdit('purchase_date')} />
                </div>
                {(editing?.component?.type || '').toLowerCase() === 'brass' && (
                  <div>
                    <label className="label">Starting Firing Count (brass)</label>
                    <input type="number" min="0" className="input" {...registerEdit('starting_firing_count')} />
                  </div>
                )}
                <div>
                  <label className="label">Purchase Location</label>
                  <input type="text" className="input" {...registerEdit('purchase_location')} />
                </div>
                <div className="md:col-span-2">
                  <label className="label">Website</label>
                  <input type="url" className="input" {...registerEdit('website')} />
                </div>
                <div className="md:col-span-2">
                  <label className="label">Notes</label>
                  <textarea className="input" rows="3" {...registerEdit('notes')} />
                </div>
                <div className="md:col-span-2 flex justify-end gap-2">
                  <button type="button" className="btn" onClick={() => { setShowEditModal(false); setEditing(null); resetEdit({}); }}>Cancel</button>
                  <button type="submit" className="btn btn-primary">Save Changes</button>
                </div>
              </form>
            </div>
          </div>
          </div>
        )}
      <ImagePreviewModal id={filePreview?.id} url={filePreview?.url} filename={filePreview?.filename} thumbnailUrl={filePreview?.thumbnailUrl} onClose={() => setFilePreview(null)} />
    </div>
  );
};

export default Purchases;